import React, { useEffect, useId, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Controller, useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';

import { getErrorMessage, getYupValidationResolver, useMount, yup } from 'shared/base';

const formSchema = yup.object({
  delimiter: yup
    .string()
    .default(',' as string)
    .when('exportType', {
      is: 'csv',
      then: (schema) => schema.required(),
    }),
  exportType: yup
    .string()
    .default('json' as 'json' | 'csv')
    .oneOf(['json', 'csv'])
    .required(),
  includeHeader: yup
    .boolean()
    .default(true as boolean)
    .label('Include header')
    .when('exportType', {
      is: 'csv',
      then: (schema) => schema.required(),
    }),
  indent: yup
    .number()
    .default(2 as number)
    .integer()
    .min(0)
    .max(10)
    .when('exportType', {
      is: 'json',
      then: (schema) => schema.required(),
    }),
  quoteAll: yup
    .boolean()
    .default(false as boolean)
    .label('Quote all')
    .when('exportType', {
      is: 'csv',
      then: (schema) => schema.required(),
    }),
  sizeOption: yup
    .string()
    .default('currentPage' as 'currentPage' | 'allPages')
    .label('Pages')
    .oneOf(['currentPage', 'allPages'])
    .required(),
});

interface Props {
  fetchOptions: {
    baseUrl: string;
    data?: string;
    method: string;
    searchParams?: Record<string, any>;
  };
  onClose: () => void;
  open: boolean;
  page?: number;
  perPage?: number;
}

export function TableDataExportDialog({ fetchOptions, onClose, open, page, perPage }: Props) {
  const dialogLabelId = useId();
  const workerRef = useRef<Worker | null>(null);
  const {
    control,
    formState: { isSubmitting },
    handleSubmit: handleFormSubmit,
  } = useForm({
    defaultValues: formSchema.getDefault(),
    resolver: getYupValidationResolver(formSchema),
  });
  const [csvLimitExceededData, setCsvLimitExceededData] = useState<
    {
      columnIndex: number;
      columnName: string | null;
      rowIndex: number;
    }[]
  >([]);

  const exportTypeValue = useWatch({ control, name: 'exportType' });

  const handleSubmit = handleFormSubmit(async (data) => {
    return new Promise((resolve, reject) => {
      try {
        if (!workerRef.current) {
          workerRef.current = new Worker(new URL('tableDataExport.js', window.location.origin));

          workerRef.current.addEventListener('message', (e) => {
            if (e.data.type === 'done') {
              const url = URL.createObjectURL(e.data.blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `export-${new Date().toISOString().split('T')[0]}.${data.exportType}`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success('Data exported successfully');
              resolve(null);
              if (e.data.csvLimitExceededData) {
                setCsvLimitExceededData(e.data.csvLimitExceededData);
              } else {
                onClose();
              }
            } else if (e.data.type === 'error') {
              toast.error(`Failed to export data: ${e.data.error}`);
              reject(e.data.error);
            }
          });

          workerRef.current.addEventListener('error', (error) => {
            toast.error(`Failed to export data: ${error.message}`);
            reject(error.message);
          });
        }

        const options = {
          delimiter: data.delimiter,
          exportType: data.exportType,
          includeHeader: data.includeHeader,
          indent: data.indent,
          page: page && data.sizeOption === 'currentPage' ? page : undefined,
          perPage: perPage && data.sizeOption === 'currentPage' ? perPage : undefined,
          quoteAll: data.quoteAll,
        };

        workerRef.current.postMessage({
          fetchOptions,
          options,
        });
      } catch (error) {
        toast.error(`Failed to export data: ${getErrorMessage(error)}`);
        reject(error);
      }
    });
  });

  useMount(() => {
    return () => {
      workerRef.current?.postMessage('terminate');
      setTimeout(() => {
        workerRef.current?.terminate();
        workerRef.current = null;
      }, 100);
    };
  });

  useEffect(() => {
    if (!open) {
      workerRef.current?.postMessage('terminate');
      setTimeout(() => {
        workerRef.current?.terminate();
        workerRef.current = null;
      }, 100);
    }
  }, [open]);

  return (
    <Dialog aria-labelledby={dialogLabelId} fullWidth={true} onClose={onClose} open={open}>
      <form onSubmit={handleSubmit}>
        <DialogTitle id={dialogLabelId}>
          <Box alignItems="center" display="flex" justifyContent="space-between">
            <Typography variant="h6">Export data</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack justifyContent="flex-start" spacing={2}>
            <Controller
              control={control}
              name="exportType"
              render={({ field }) => (
                <FormControl>
                  <Typography component="label" variant="body1Bold">
                    Format
                  </Typography>
                  <RadioGroup
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    row={true}
                    {...field}
                  >
                    <FormControlLabel control={<Radio />} label="JSON" value="json" />
                    <FormControlLabel control={<Radio />} label="CSV" value="csv" />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Stack alignItems="flex-start" spacing={2}>
              {!!page && !!perPage && (
                <Controller
                  control={control}
                  name="sizeOption"
                  render={({ field }) => (
                    <TextField fullWidth={true} label="Pages" select={true} size="small" {...field}>
                      <MenuItem value="currentPage">Current page</MenuItem>
                      <MenuItem value="allPages">All pages</MenuItem>
                    </TextField>
                  )}
                />
              )}
              <Typography variant="body2">Options</Typography>
              {exportTypeValue === 'csv' && (
                <>
                  <Controller
                    control={control}
                    name="delimiter"
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        error={!!error}
                        fullWidth={true}
                        helperText={error?.message}
                        label="Delimiter"
                        size="small"
                        {...field}
                      />
                    )}
                  />
                  <Stack direction="row" spacing={2}>
                    <Controller
                      control={control}
                      name="includeHeader"
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox {...field} checked={field.value} sx={{ marginLeft: -1 }} />
                          }
                          label="Include header"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="quoteAll"
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={field.value} />}
                          label="Quote all"
                        />
                      )}
                    />
                  </Stack>
                </>
              )}
              {exportTypeValue === 'json' && (
                <>
                  <Controller
                    control={control}
                    name="indent"
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        error={!!error}
                        fullWidth={true}
                        helperText={error?.message}
                        label="Indent"
                        {...field}
                      />
                    )}
                  />
                </>
              )}
            </Stack>
            {csvLimitExceededData.length > 0 && (
              <Alert
                severity="warning"
                sx={{
                  marginBottom: 2,
                  width: '100%',
                }}
                variant="filled"
              >
                <AlertTitle>Large values detected</AlertTitle>
                The content in the following cells exceeds the 32,767-character limit set by some
                spreadsheet applications:
                <List sx={{ paddingLeft: 2 }}>
                  {csvLimitExceededData.slice(0, 3).map((data) => (
                    <ListItem
                      key={`${data.rowIndex}-${data.columnIndex}`}
                      sx={{ display: 'list-item', listStyleType: 'disc', padding: 0 }}
                    >
                      Row {data.rowIndex + 1}, Column {data.columnIndex + 1}{' '}
                      {data.columnName && `(${data.columnName})`}
                    </ListItem>
                  ))}
                  {csvLimitExceededData.length > 3 && (
                    <ListItem sx={{ display: 'list-item', listStyleType: 'disc', padding: 0 }}>
                      ... and {csvLimitExceededData.length - 3} more.
                    </ListItem>
                  )}
                </List>
                All data was exported successfully, but you may want to refine your query to avoid
                display issues in spreadsheet applications.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          {isSubmitting && (
            <Alert severity="warning" sx={{ marginBottom: 2, width: '100%' }} variant="filled">
              <AlertTitle>Exporting data</AlertTitle>
              Please don&apos;t close the popup while the export is running.
            </Alert>
          )}
          <Button color="secondary" onClick={onClose} size="medium" variant="outlined">
            Cancel
          </Button>
          <Button loading={isSubmitting} size="medium" type="submit" variant="contained">
            Export
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
